import { choraleFile } from './chorales';
import { sonataFile } from './sonatas';
import { decodeHarmony, decodeMeasures } from './chorales/codec';
import { COUNTED, CORPUS, SONATA_ENTRIES, VARIATION_ENTRIES } from './chorales/corpusIndex';
import type { CorpusEntry, ExcerptRef } from './chorales/corpusTypes';
import type { CadenceKind } from './chorales/derive';
import { Key, keyName, keyRelation, parseKey, sameKey } from '../music/key';
import { transposePitch } from '../music/pitch';

/**
 * Corpus statistics for the Observatory. Every count keeps the excerpts it counts, so the page
 * can play them. Everything is computed from the corpus index and the stored harmony labels,
 * deterministically, in the browser.
 */

export type ModeFilter = 'all' | 'major' | 'minor';
/** Which body of music to count: Bach's chorales or Mozart's sonata movements. */
export type CorpusName = 'bach' | 'mozart' | 'variations';

export interface Tally {
  id: string;
  count: number;
  share: number;
  refs: ExcerptRef[];
}

function tallies(map: Map<string, ExcerptRef[]>, total?: number): Tally[] {
  const sum = total ?? [...map.values()].reduce((a, l) => a + l.length, 0);
  return [...map.entries()].map(([id, refs]) => ({ id, count: refs.length, share: sum ? refs.length / sum : 0, refs })).sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
}

const push = (map: Map<string, ExcerptRef[]>, id: string, ref: ExcerptRef) => {
  const list = map.get(id) ?? [];
  list.push(ref);
  map.set(id, list);
};

export function chorales(mode: ModeFilter, corpus: CorpusName = 'bach'): CorpusEntry[] {
  return (corpus === 'bach' ? COUNTED : corpus === 'mozart' ? SONATA_ENTRIES : VARIATION_ENTRIES).filter((e) => mode === 'all' || parseKey(e.homeKey).mode === mode);
}

const harmonyFile = (id: string) => choraleFile(id) ?? sonataFile(id);

/** From one beat before the approach chord to the end of the arrival. */
function cadenceRef(e: CorpusEntry, i: number): ExcerptRef {
  const [at, dur, , key, approach] = e.cadences[i];
  const start = Math.max(e.phrases[i]?.[0] ?? 0, approach - 1);
  return [e.id, start, at + dur, key];
}

export function totals(mode: ModeFilter, corpus: CorpusName = 'bach') {
  const list = chorales(mode, corpus);
  return {
    chorales: list.length,
    phrases: list.reduce((a, e) => a + e.phrases.length, 0),
    chords: list.reduce((a, e) => a + (harmonyFile(e.id)?.harmony.length ?? 0), 0),
    paths: list.reduce((a, e) => a + e.matches.length, 0),
  };
}

/** How lines end: the kind of cadence at every fermata. */
export function cadenceKinds(mode: ModeFilter, corpus: CorpusName = 'bach'): Tally[] {
  const map = new Map<string, ExcerptRef[]>();
  for (const e of chorales(mode, corpus)) e.cadences.forEach((c, i) => push(map, c[2], cadenceRef(e, i)));
  return tallies(map);
}

/** Where lines land: the key of each phrase ending, named from the home key ("V", "vi", "III"). */
export function landings(home: 'major' | 'minor', corpus: CorpusName = 'bach'): Tally[] {
  const map = new Map<string, ExcerptRef[]>();
  for (const e of chorales(home, corpus)) {
    const h = parseKey(e.homeKey);
    e.cadences.forEach((c, i) => push(map, keyRelation(h, parseKey(c[3])), cadenceRef(e, i)));
  }
  return tallies(map);
}

/** The key a relation names when home is C major or A minor ("V" → G major). */
export function exampleKey(relation: string, home: 'major' | 'minor'): string {
  const acc = relation.match(/^[#b]*/)![0];
  const numeral = relation.slice(acc.length);
  const degree = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii'].indexOf(numeral.toLowerCase()) + 1;
  if (degree < 1) return relation;
  const tonic: Key = home === 'major' ? { tonic: { letter: 0, alter: 0 }, mode: 'major' } : { tonic: { letter: 5, alter: 0 }, mode: 'minor' };
  const scale = home === 'major' ? [0, 2, 4, 5, 7, 9, 11] : [0, 2, 3, 5, 7, 8, 10];
  const shift = [...acc].reduce((a, ch) => a + (ch === '#' ? 1 : -1), 0);
  const p = transposePitch({ ...tonic.tonic, octave: 4 }, degree - 1, scale[degree - 1] + shift);
  const minor = numeral === numeral.toLowerCase();
  return keyName({ tonic: { letter: p.letter, alter: p.alter }, mode: minor ? 'minor' : 'major' });
}

export interface Endings {
  /** Chorales whose last line is an authentic cadence at home. */
  home: ExcerptRef[];
  total: number;
  /** Minor-key chorales that end on a major chord (a Picardy third). */
  picardy: ExcerptRef[];
  minorTotal: number;
  /** Chorales whose first line already ends away from the home key. */
  firstAway: ExcerptRef[];
}

export function endings(mode: ModeFilter, corpus: CorpusName = 'bach'): Endings {
  const out: Endings = { home: [], total: 0, picardy: [], minorTotal: 0, firstAway: [] };
  for (const e of chorales(mode, corpus)) {
    const h = parseKey(e.homeKey);
    const last = e.cadences.length - 1;
    if (last < 0) continue;
    out.total++;
    const [, , kind, key, , , arrival] = e.cadences[last];
    if ((kind === 'pac' || kind === 'iac') && sameKey(parseKey(key), h)) out.home.push(cadenceRef(e, last));
    if (h.mode === 'minor') {
      out.minorTotal++;
      if (sameKey(parseKey(key), h) && /^I(?![IViv])/.test(arrival)) out.picardy.push(cadenceRef(e, last));
    }
    if (e.cadences.length > 1 && !sameKey(parseKey(e.cadences[0][3]), h)) out.firstAway.push(cadenceRef(e, 0));
  }
  return out;
}

/** A chord label reduced to its family: inversion and seventh dropped ("V65" → "V", "viiø7/V" → "viio/V"). */
export function chordFamily(rn: string): string {
  if (/^Cad/.test(rn)) return 'Cad64';
  if (/^(It|Fr|Ger)/.test(rn)) return 'Aug6';
  return rn
    .split('/')
    .map((part, i) => (i === 0 ? part.replace(/\d+$/, '').replace('ø', 'o') : part))
    .join('/');
}

interface Chord {
  t: number;
  end: number;
  key: string;
  family: string;
}

const chordsCache = new Map<string, Chord[]>();
/** The chord labels of a chorale as timed chords, repeated labels of the same family merged. */
export function chordsOf(id: string): Chord[] {
  let out = chordsCache.get(id);
  if (out) return out;
  out = [];
  const file = harmonyFile(id);
  if (file) {
    const measures = decodeMeasures(file);
    const start = new Map(measures.map((m) => [m.bar, m.start]));
    const total = measures[measures.length - 1].start + measures[measures.length - 1].length;
    const timed = decodeHarmony(file)
      .filter((h) => h.key && start.has(h.bar))
      .map((h) => ({ t: start.get(h.bar)! + h.beat, key: h.key!, family: chordFamily(h.rn) }))
      .sort((a, b) => a.t - b.t);
    timed.forEach((c, i) => {
      const end = i + 1 < timed.length ? timed[i + 1].t : total;
      const prev = out![out!.length - 1];
      if (prev && prev.family === c.family && prev.key === c.key) prev.end = end;
      else out!.push({ ...c, end });
    });
  }
  chordsCache.set(id, out);
  return out;
}

const transitionCache = new Map<string, Map<string, Map<string, ExcerptRef[]>>>();
/** In major or minor keys (the local key of each chord), which chord follows which. */
export function transitions(localMode: 'major' | 'minor', mode: ModeFilter, corpus: CorpusName = 'bach'): Map<string, Map<string, ExcerptRef[]>> {
  const cacheKey = `${localMode}|${mode}|${corpus}`;
  let map = transitionCache.get(cacheKey);
  if (map) return map;
  map = new Map();
  for (const e of chorales(mode, corpus)) {
    const list = chordsOf(e.id);
    for (let i = 0; i + 1 < list.length; i++) {
      const a = list[i];
      const b = list[i + 1];
      if (a.key !== b.key || parseKey(a.key).mode !== localMode) continue;
      // Excerpt: up to two beats of each chord around the change.
      const ref: ExcerptRef = [e.id, Math.max(a.t, b.t - 2), Math.min(b.end, b.t + 2), a.key];
      const inner = map.get(a.family) ?? new Map<string, ExcerptRef[]>();
      push(inner, b.family, ref);
      map.set(a.family, inner);
    }
  }
  transitionCache.set(cacheKey, map);
  return map;
}

/** Chord families ordered by how often they lead somewhere. */
export function commonChords(localMode: 'major' | 'minor', mode: ModeFilter, n = 10, corpus: CorpusName = 'bach'): Array<{ family: string; count: number }> {
  return [...transitions(localMode, mode, corpus).entries()]
    .map(([family, next]) => ({ family, count: [...next.values()].reduce((a, l) => a + l.length, 0) }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}

export function nextChords(family: string, localMode: 'major' | 'minor', mode: ModeFilter, corpus: CorpusName = 'bach'): Tally[] {
  return tallies(transitions(localMode, mode, corpus).get(family) ?? new Map());
}

/** How many times each path was found. */
export function pathCounts(mode: ModeFilter, corpus: CorpusName = 'bach'): Tally[] {
  const map = new Map<string, ExcerptRef[]>();
  for (const e of chorales(mode, corpus)) for (const [path, start, end, key] of e.matches) push(map, path, [e.id, start, end, key]);
  return tallies(map);
}

export function breath(mode: ModeFilter, corpus: CorpusName = 'bach') {
  return corpus === 'bach' ? CORPUS.breath[mode] : corpus === 'mozart' ? CORPUS.sonataBreath[mode] : CORPUS.variationBreath[mode];
}

export const CADENCE_WORDS: Record<CadenceKind, string> = {
  pac: 'Arrives, melody on the keynote',
  iac: 'Arrives, melody elsewhere',
  half: 'Stops on a question',
  phrygian: 'Sinks onto a question',
  deceptive: 'The ground gives way',
  plagal: 'A soft amen',
  tonic: 'Settles quietly',
  pause: 'Just a breath',
};
